import {
  MEDIA_TYPE_LABELS,
  MediaItem,
  MediaType,
  OkResponse,
  POST_PLACE_LABELS,
  PostPlace,
  formatDate,
  formatIsoDate,
  isoDateInSaoPaulo,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Overlays';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { formatBytes } from '../../lib/backups-api';
import {
  formatClip,
  formatShape,
  mediaNote,
  mediaTitle,
  useInvalidateMedia,
} from '../../lib/media-api';
import { errorMessage } from '../../lib/messages';
import { parseBrDate } from '../../lib/numbers';
import { useIsAdmin } from '../../lib/session';
import { Combobox } from '../../components/Combobox';
import { productVariantOption } from '../../components/pickers';

const StarIcon = ({ size = 14, filled = true }: { size?: number; filled?: boolean }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth={filled ? 0 : 2}
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m12 3 2.8 5.8 6.2.9-4.5 4.4 1 6.3L12 17.5 6.5 20.4l1-6.3L3 9.7l6.2-.9z" />
  </svg>
);

const PlayIcon = ({ size = 20 }: { size?: number }) => (
  <span className="play">
    <span>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#FFF6EC" aria-hidden="true">
        <path d="M8 5v14l11-7z" />
      </svg>
    </span>
  </span>
);

/** Grid tile: thumbnail, type tag, cover star, video duration, processing state. */
export function MediaTile({
  item,
  selected,
  onPick,
  tag,
  aspect,
  corner,
  drag,
  showCover = true,
}: {
  item: MediaItem;
  selected: boolean;
  onPick: () => void;
  tag: string;
  /** CSS aspect-ratio; the library uses the real one. */
  aspect: string;
  corner?: ReactNode;
  /** The library does not mark covers. */
  showCover?: boolean;
  drag?: {
    onDragStart: () => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: () => void;
  };
}) {
  const cover = showCover && item.isProductCover;
  return (
    <button
      type="button"
      className="thumb"
      onClick={onPick}
      aria-pressed={selected}
      aria-label={`${mediaTitle(item)}${item.variant ? `, variação ${item.variant.name}` : ''}`}
      draggable={!!drag}
      {...(drag
        ? {
            onDragStart: drag.onDragStart,
            onDragOver: drag.onDragOver,
            onDrop: (e: React.DragEvent) => {
              e.preventDefault();
              drag.onDrop();
            },
          }
        : {})}
      style={{
        aspectRatio: aspect,
        width: '100%',
        padding: 0,
        cursor: 'pointer',
        border: cover ? '2px solid var(--co)' : '1px solid var(--line)',
        outline: selected ? '3px solid var(--pe)' : 0,
        outlineOffset: 2,
      }}
    >
      {item.thumbUrl ? (
        <img
          src={item.thumbUrl}
          alt=""
          loading="lazy"
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <div className="ph" style={{ position: 'absolute', inset: 0, border: 0, borderRadius: 0 }}>
          {item.status === 'failed'
            ? 'Não foi possível processar'
            : item.kind === 'video'
              ? 'Processando vídeo…'
              : 'Processando…'}
        </div>
      )}
      <span className="mtag">{tag}</span>
      {corner ??
        (cover ? (
          <span className="star" aria-hidden="true">
            <StarIcon />
          </span>
        ) : null)}
      {item.kind === 'video' && item.status === 'ready' ? (
        <>
          <PlayIcon />
          {item.durationSeconds !== null ? (
            <span className="dur">{formatClip(item.durationSeconds)}</span>
          ) : null}
        </>
      ) : null}
      {item.status === 'failed' ? (
        <span className="dur" style={{ background: 'var(--err)' }}>
          Falhou
        </span>
      ) : null}
    </button>
  );
}

function Check({ on, label, onToggle }: { on: boolean; label: string; onToggle: () => void }) {
  return (
    <button type="button" className="check sm" aria-pressed={on} onClick={onToggle}>
      <span className={on ? 'box on' : 'box'}>
        {on ? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
        ) : null}
      </span>
      {label}
    </button>
  );
}

function PostDialog({
  item,
  onClose,
  onSaved,
}: {
  item: MediaItem;
  onClose: () => void;
  onSaved: (m: MediaItem) => void;
}) {
  const [where, setWhere] = useState<PostPlace>(
    item.kind === 'video' ? 'ig_reels' : item.type === 'product' ? 'ml_listing' : 'ig_feed',
  );
  const [date, setDate] = useState(formatIsoDate(isoDateInSaoPaulo()));
  const [link, setLink] = useState('');
  const iso = parseBrDate(date);
  const linkOk = !link.trim() || /^https?:\/\/\S+$/i.test(link.trim());
  const save = useMutation({
    mutationFn: () =>
      api.post(`/media/${item.id}/posts`, MediaItem, {
        where,
        postedOn: iso,
        url: link.trim() || null,
      }),
    onSuccess: onSaved,
  });
  return (
    <Modal
      title="Registrar postagem"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={!iso || !linkOk || save.isPending}
            onClick={() => save.mutate()}
          >
            Registrar
          </button>
        </>
      }
    >
      <p className="sm muted">
        {mediaTitle(item)} · {item.variant?.name ?? item.product.name}
      </p>
      <div className="field">
        <label htmlFor="pw">Onde</label>
        <select
          id="pw"
          className="input"
          value={where}
          onChange={(e) => setWhere(PostPlace.parse(e.target.value))}
        >
          {PostPlace.options.map((p) => (
            <option key={p} value={p}>
              {POST_PLACE_LABELS[p]}
            </option>
          ))}
        </select>
      </div>
      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        }}
      >
        <div className="field">
          <label htmlFor="pd">Data</label>
          <input
            id="pd"
            className="input"
            inputMode="numeric"
            placeholder="DD/MM/AAAA"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            aria-invalid={!iso}
          />
          {!iso ? <span className="help err">Use DD/MM/AAAA.</span> : null}
        </div>
        <div className="field">
          <label htmlFor="pl">Link (opcional)</label>
          <input
            id="pl"
            className="input"
            placeholder="https://…"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            aria-invalid={!linkOk}
          />
          {!linkOk ? <span className="help err">Cole o link completo, com https://</span> : null}
        </div>
      </div>
      {save.error ? (
        <p className="sm" style={{ color: 'var(--err)' }}>
          {errorMessage(save.error)}
        </p>
      ) : null}
    </Modal>
  );
}

function ConfirmDialog({
  title,
  text,
  action,
  onClose,
  onConfirm,
  pending,
}: {
  title: string;
  text: string;
  action: string;
  onClose: () => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn d" disabled={pending} onClick={onConfirm}>
            {action}
          </button>
        </>
      }
    >
      <p>{text}</p>
    </Modal>
  );
}

/**
 * Detail of one photo or video (board Produto-Visao, Mídia aside; also the library drawer):
 * cover, order, type, size, approvals, where it was posted, delete; Admin: keep only the preview.
 */
export function MediaDetail({
  item,
  position,
  total,
  variants,
  onClose,
  showProduct,
  bare,
}: {
  item: MediaItem;
  /** 1-based place in the product gallery (null hides the order controls). */
  position: number | null;
  total: number;
  variants: { id: string; name: string }[] | null;
  onClose: () => void;
  showProduct?: boolean;
  /** Inside a drawer, which has its own title and close button. */
  bare?: boolean;
}) {
  const toast = useToast();
  const isAdmin = useIsAdmin();
  const refresh = useInvalidateMedia();
  const [dialog, setDialog] = useState<'post' | 'delete' | 'prune' | null>(null);
  const done = (msg?: string) => {
    refresh();
    if (msg) toast(msg);
  };
  const onError = (err: unknown) => toast(errorMessage(err));
  const patch = useMutation({
    mutationFn: (body: object) => api.patch(`/media/${item.id}`, MediaItem, body),
    onSuccess: () => done(),
    onError,
  });
  const cover = useMutation({
    mutationFn: (scope: 'product' | 'variant') =>
      api.post(`/media/${item.id}/make-cover`, MediaItem, { scope }),
    onSuccess: (_m, scope) =>
      done(scope === 'product' ? 'Capa do produto definida' : 'Capa da variação definida'),
    onError,
  });
  const move = useMutation({
    mutationFn: (direction: 'up' | 'down') =>
      api.post(`/media/${item.id}/move`, OkResponse, { direction }),
    onSuccess: () => done(),
    onError,
  });
  const reprocess = useMutation({
    mutationFn: () => api.post(`/media/${item.id}/reprocess`, MediaItem),
    onSuccess: () => done('Processando de novo'),
    onError,
  });
  const removePost = useMutation({
    mutationFn: (postId: string) => api.delete(`/media/${item.id}/posts/${postId}`, MediaItem),
    onSuccess: () => done('Postagem removida'),
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/media/${item.id}`, OkResponse),
    onSuccess: () => {
      setDialog(null);
      onClose();
      done('Mídia excluída');
    },
    onError,
  });
  const prune = useMutation({
    mutationFn: () => api.post(`/media/${item.id}/prune-original`, MediaItem),
    onSuccess: () => {
      setDialog(null);
      done(`Original apagado: ${formatBytes(item.originalBytes)} liberados`);
    },
    onError,
  });
  const title = mediaTitle(item);
  const ready = item.status === 'ready';

  return (
    <div className="col" style={{ gap: 12 }}>
      {bare ? null : (
        <div className="row between">
          <h2 style={{ fontSize: 18 }}>{title}</h2>
          <button type="button" className="btn sm g" aria-label="Fechar detalhes" onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
      )}
      {item.kind === 'video' && ready && item.videoUrl ? (
        // oxlint-disable-next-line jsx-a11y/media-has-caption -- timelapses and product clips have no speech
        <video
          key={item.id}
          src={item.videoUrl}
          poster={item.displayUrl ?? undefined}
          controls
          playsInline
          preload="none"
          style={{ width: '100%', maxHeight: 360, borderRadius: 12, background: '#1D1D1D' }}
        />
      ) : item.displayUrl ? (
        <a href={item.displayUrl} target="_blank" rel="noreferrer">
          <img
            src={item.displayUrl}
            alt={title}
            style={{
              width: '100%',
              maxHeight: 420,
              objectFit: 'contain',
              borderRadius: 12,
              background: 'var(--sunk)',
            }}
          />
        </a>
      ) : (
        <div className="ph" style={{ height: 200 }}>
          {item.status === 'failed' ? 'Não foi possível processar' : 'Processando…'}
        </div>
      )}
      {item.status === 'failed' ? (
        <div className="alert err">
          <Icon name="alert" className="ic" />
          <div className="grow col" style={{ gap: 4 }}>
            <p className="sm">
              <b>Não conseguimos processar este arquivo.</b>
            </p>
            {item.error ? <p className="xs">{item.error}</p> : null}
          </div>
          <button
            type="button"
            className="btn sm"
            disabled={reprocess.isPending}
            onClick={() => reprocess.mutate()}
          >
            Tentar de novo
          </button>
        </div>
      ) : null}
      {item.status === 'queued' || item.status === 'processing' ? (
        <p className="xs muted">
          {item.kind === 'video'
            ? 'Gerando o quadro de capa e a prévia comprimida…'
            : 'Ajustando o tamanho da foto…'}
        </p>
      ) : (
        <p className="xs muted">{mediaNote(item)}</p>
      )}

      <div className="row wrap" style={{ gap: 8 }}>
        {item.isProductCover ? (
          <span className="badge co">
            <StarIcon />
            Capa do produto
          </span>
        ) : (
          <button
            type="button"
            className="btn sm"
            disabled={!ready || cover.isPending}
            onClick={() => cover.mutate('product')}
          >
            <StarIcon size={16} filled={false} />
            Usar como capa
          </button>
        )}
        {item.variant ? (
          item.isVariantCover ? (
            <span className="badge">Capa da {item.variant.name}</span>
          ) : (
            <button
              type="button"
              className="btn sm g"
              disabled={!ready || cover.isPending}
              onClick={() => cover.mutate('variant')}
            >
              Capa da {item.variant.name}
            </button>
          )
        ) : null}
        {position !== null ? (
          <>
            <button
              type="button"
              className="btn sm icon"
              aria-label="Mover para antes"
              disabled={position <= 1 || move.isPending}
              onClick={() => move.mutate('up')}
            >
              ↑
            </button>
            <button
              type="button"
              className="btn sm icon"
              aria-label="Mover para depois"
              disabled={position >= total || move.isPending}
              onClick={() => move.mutate('down')}
            >
              ↓
            </button>
            <span className="xs muted">
              posição {position} de {total}
            </span>
          </>
        ) : null}
      </div>

      <div>
        {showProduct ? (
          <div className="kv sm">
            <span className="muted">Produto</span>
            <span className="v">
              <Link to={`/produtos/${item.product.id}/midia`}>{item.product.name}</Link>
            </span>
          </div>
        ) : null}
        <div className="kv sm">
          <span className="muted">Tipo</span>
          <select
            className="input"
            aria-label="Tipo"
            style={{ height: 32, width: 'auto', fontSize: 14 }}
            value={item.type}
            onChange={(e) => patch.mutate({ type: MediaType.parse(e.target.value) })}
          >
            {MediaType.options.map((t) => (
              <option key={t} value={t}>
                {MEDIA_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="kv sm">
          <span className="muted">Formato</span>
          <span className="v">{formatShape(item)}</span>
        </div>
        {item.kind === 'video' && item.durationSeconds !== null ? (
          <div className="kv sm">
            <span className="muted">Duração</span>
            <span className="v">{formatClip(item.durationSeconds)}</span>
          </div>
        ) : null}
        <div className="kv sm">
          <span className="muted">Gravado</span>
          <span className="v">{item.capturedAt ? formatDate(item.capturedAt) : '—'}</span>
        </div>
        <div className="kv sm">
          <span className="muted">Variação</span>
          {variants ? (
            <Combobox
              size="sm"
              ariaLabel="Variação"
              style={{ width: 'auto', minWidth: 170 }}
              value={item.variant?.id ?? ''}
              options={[
                { value: '', label: 'Todas (produto)', pinned: 'top' },
                ...variants.map(productVariantOption),
              ]}
              onChange={(v) => patch.mutate({ variantId: v || null })}
            />
          ) : (
            <span className="v">{item.variant?.name ?? 'Todas'}</span>
          )}
        </div>
        <div className="kv sm">
          <span className="muted">Impressão</span>
          <span className="v">
            {item.printJobRef ?? (
              <span className="muted" title="Chega com a fila de impressão">
                —
              </span>
            )}
          </span>
        </div>
        {item.type === 'customer' ? (
          <div className="kv sm">
            <span className="muted">Autorização do cliente</span>
            <select
              className="input"
              aria-label="Autorização do cliente"
              style={{ height: 32, width: 'auto', fontSize: 14 }}
              value={item.customerConsent}
              onChange={(e) => patch.mutate({ customerConsent: e.target.value })}
            >
              <option value="pending">Pendente</option>
              <option value="granted">Autorizou</option>
              <option value="not_needed">Não precisa</option>
            </select>
          </div>
        ) : null}
      </div>

      <Check
        on={item.approvedSocial}
        label="Aprovado para redes sociais"
        onToggle={() => patch.mutate({ approvedSocial: !item.approvedSocial })}
      />
      <Check
        on={item.approvedMarketplace}
        label="Aprovado para marketplace"
        onToggle={() => patch.mutate({ approvedMarketplace: !item.approvedMarketplace })}
      />
      {item.type === 'customer' && item.customerConsent === 'pending' ? (
        <p className="xs" style={{ color: 'var(--warn)' }}>
          Peça a autorização do cliente antes de postar.
        </p>
      ) : null}

      <div className="card sunk col" style={{ gap: 6, padding: 12, borderRadius: 12 }}>
        <div className="row between">
          <span className="lbl">Onde foi postado</span>
          <button type="button" className="btn sm g" onClick={() => setDialog('post')}>
            + Registrar postagem
          </button>
        </div>
        {item.posts.map((p) => (
          <div key={p.id} className="kv sm">
            <span>
              {p.url ? (
                <a href={p.url} target="_blank" rel="noreferrer">
                  {POST_PLACE_LABELS[p.where]}
                </a>
              ) : (
                POST_PLACE_LABELS[p.where]
              )}
            </span>
            <span className="row" style={{ gap: 4 }}>
              <span className="v">{formatIsoDate(p.postedOn)}</span>
              <button
                type="button"
                className="btn sm g icon"
                aria-label={`Remover postagem em ${POST_PLACE_LABELS[p.where]}`}
                onClick={() => removePost.mutate(p.id)}
              >
                ×
              </button>
            </span>
          </div>
        ))}
        {item.posts.length === 0 ? <p className="sm muted">Ainda não postado.</p> : null}
      </div>

      <div className="row wrap" style={{ gap: 8 }}>
        {item.originalUrl ? (
          <a className="btn sm" href={`${item.originalUrl}?download=1`}>
            Baixar original
          </a>
        ) : null}
        {isAdmin && item.kind === 'video' && ready && !item.originalPruned ? (
          <button type="button" className="btn sm" onClick={() => setDialog('prune')}>
            Guardar só a prévia
          </button>
        ) : null}
        <button type="button" className="btn sm d" onClick={() => setDialog('delete')}>
          Excluir
        </button>
      </div>

      {dialog === 'post' ? (
        <PostDialog
          item={item}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            done('Postagem registrada');
          }}
        />
      ) : null}
      {dialog === 'delete' ? (
        <ConfirmDialog
          title={`Excluir “${title}”?`}
          text="O original e a prévia saem do armazenamento. O registro de onde foi postado também some."
          action="Excluir"
          pending={remove.isPending}
          onClose={() => setDialog(null)}
          onConfirm={() => remove.mutate()}
        />
      ) : null}
      {dialog === 'prune' ? (
        <ConfirmDialog
          title="Guardar só a prévia?"
          text={`O vídeo original (${formatBytes(item.originalBytes)}) é apagado de vez, inclusive dos próximos backups. Fica a prévia comprimida${item.previewBytes !== null ? ` (${formatBytes(item.previewBytes)})` : ''}, boa para redes sociais, mas sem a qualidade original.`}
          action="Apagar o original"
          pending={prune.isPending}
          onClose={() => setDialog(null)}
          onConfirm={() => prune.mutate()}
        />
      ) : null}
    </div>
  );
}
